import React, { useMemo, useRef, useState } from "react";
import Header from "../Component/Header";
import Breadcrumb from "../Component/Breadcrumb";
import Footer from "../Component/Footer";
import Cropper from "react-easy-crop";

const GOOGLE_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbwQqbBMPA6eFqeE-3_SMm6qsPuOnNMkjhlNqTl4kS4ik4Z4Zy_eFDY9CXaOqKNPZ3HS2g/exec";

const streams = ["Commerce", "Science", "Arts"];
const genders = ["Male", "Female", "Other"];

const AdmissionClassXI = () => {
  const formRef = useRef(null);

  const [sameSchoolSiblings, setSameSchoolSiblings] = useState([
    { admNo: "", name: "", className: "" },
  ]);
  const [otherSchoolSiblings, setOtherSchoolSiblings] = useState([
    { schoolName: "", name: "", className: "" },
  ]);

  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");

  // ---------------- PHOTO (preview + crop) ----------------
  const [photoSrc, setPhotoSrc] = useState("");
  const [showCrop, setShowCrop] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [photoCroppedBase64, setPhotoCroppedBase64] = useState("");
  const [photoFileName, setPhotoFileName] = useState("");
  const [photoMimeType, setPhotoMimeType] = useState("");

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.crossOrigin = "anonymous";
      img.src = url;
    });

  const getCroppedImageBase64 = async (
    imageSrc,
    pixelCrop,
    mime = "image/jpeg"
  ) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return canvas.toDataURL(mime, 0.9);
  };

  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors((p) => ({ ...p, photo: "Please upload an image file." }));
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setErrors((p) => ({ ...p, photo: "Photo should be <= 3MB." }));
      return;
    }

    setErrors((p) => {
      const c = { ...p };
      delete c.photo;
      return c;
    });

    setPhotoFileName(file.name);
    setPhotoMimeType(file.type || "image/jpeg");

    const dataUrl = await readFileAsDataURL(file);
    setPhotoSrc(dataUrl);
    setShowCrop(true);
  };

  const saveCroppedPhoto = async () => {
    if (!photoSrc || !croppedAreaPixels) return;
    const out = await getCroppedImageBase64(
      photoSrc,
      croppedAreaPixels,
      photoMimeType || "image/jpeg"
    );
    setPhotoCroppedBase64(out);
    setShowCrop(false);
  };

  const removePhoto = () => {
    setPhotoSrc("");
    setPhotoCroppedBase64("");
    setPhotoFileName("");
    setPhotoMimeType("");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  // ---------------- 10th MARKSHEET ----------------
  const [marksheetBase64, setMarksheetBase64] = useState("");
  const [marksheetFileName, setMarksheetFileName] = useState("");
  const [marksheetMimeType, setMarksheetMimeType] = useState("");

  const handleMarksheetPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ok = file.type === "application/pdf" || file.type.startsWith("image/");
    if (!ok) {
      setErrors((p) => ({
        ...p,
        marksheet: "Upload PDF or an image (JPG/PNG).",
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((p) => ({ ...p, marksheet: "Marksheet should be <= 5MB." }));
      return;
    }

    setErrors((p) => {
      const c = { ...p };
      delete c.marksheet;
      return c;
    });

    setMarksheetFileName(file.name);
    setMarksheetMimeType(file.type);

    const dataUrl = await readFileAsDataURL(file);
    setMarksheetBase64(dataUrl);
  };

  const removeMarksheet = () => {
    setMarksheetBase64("");
    setMarksheetFileName("");
    setMarksheetMimeType("");
  };

  // ---------------- siblings json ----------------
  const sameSchoolJson = useMemo(
    () =>
      JSON.stringify(
        sameSchoolSiblings
          .map((s) => ({
            admNo: s.admNo?.trim(),
            name: s.name?.trim(),
            className: s.className?.trim(),
          }))
          .filter((s) => s.admNo || s.name || s.className)
      ),
    [sameSchoolSiblings]
  );

  const otherSchoolJson = useMemo(
    () =>
      JSON.stringify(
        otherSchoolSiblings
          .map((s) => ({
            schoolName: s.schoolName?.trim(),
            name: s.name?.trim(),
            className: s.className?.trim(),
          }))
          .filter((s) => s.schoolName || s.name || s.className)
      ),
    [otherSchoolSiblings]
  );

  const updateSame = (idx, key, value) => {
    setSameSchoolSiblings((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  const updateOther = (idx, key, value) => {
    setOtherSchoolSiblings((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: value };
      return copy;
    });
  };

  const addSame = () =>
    setSameSchoolSiblings((p) => [...p, { admNo: "", name: "", className: "" }]);

  const removeSameRow = (idx) =>
    setSameSchoolSiblings((p) => p.filter((_, i) => i !== idx));

  const addOther = () =>
    setOtherSchoolSiblings((p) => [
      ...p,
      { schoolName: "", name: "", className: "" },
    ]);

  const removeOtherRow = (idx) =>
    setOtherSchoolSiblings((p) => p.filter((_, i) => i !== idx));

  const isAnyFilled = (obj) =>
    Object.values(obj).some((v) => String(v || "").trim() !== "");

  const isAllFilled = (obj) =>
    Object.values(obj).every((v) => String(v || "").trim() !== "");

  const validate = () => {
    const nextErrors = {};

    sameSchoolSiblings.forEach((sib, idx) => {
      const row = { admNo: sib.admNo, name: sib.name, className: sib.className };
      if (isAnyFilled(row) && !isAllFilled(row)) {
        nextErrors[`sameSibling_${idx}`] =
          "Please fill GR NO, NAME and CLASS for this sibling row (or clear it).";
      }
    });

    otherSchoolSiblings.forEach((sib, idx) => {
      const row = {
        schoolName: sib.schoolName,
        name: sib.name,
        className: sib.className,
      };
      if (isAnyFilled(row) && !isAllFilled(row)) {
        nextErrors[`otherSibling_${idx}`] =
          "Please fill SCHOOL NAME, NAME and CLASS for this sibling row (or clear it).";
      }
    });

    // DOB not in future
    const dob = formRef.current?.elements?.dob?.value;
    if (dob) {
      const dobDate = new Date(dob);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dobDate > today) nextErrors.dob = "DOB cannot be a future date.";
    }

    // 10-digit mobile validation
    const mobileFields = ["studentMobile", "fatherMobile", "motherMobile"];
    mobileFields.forEach((name) => {
      const val = formRef.current?.elements?.[name]?.value || "";
      if (val && !/^\d{10}$/.test(val)) {
        nextErrors[name] = "Please enter a valid 10-digit mobile number.";
      }
    });

    // required uploads
    if (!photoCroppedBase64)
      nextErrors.photo = "Please upload and crop the student photo.";
    if (!marksheetBase64)
      nextErrors.marksheet = "Please upload 10th marksheet.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };
const onSubmit = (e) => {
  e.preventDefault();
  setSuccessMsg("");

  if (!formRef.current.checkValidity()) {
    formRef.current.reportValidity();
    return;
  }

  const ok = validate();
  if (!ok) return;

  // submit
formRef.current.submit();

setSuccessMsg("Form submitted successfully!");
setTimeout(() => {
  formRef.current.reset();
  setSameSchoolSiblings([{ admNo: "", name: "", className: "" }]);
  setOtherSchoolSiblings([{ schoolName: "", name: "", className: "" }]);
  setErrors({});
  removePhoto();
  removeMarksheet();
}, 1500);
};


  return (
    <>
      <Header />

      <Breadcrumb
        title="Admission Class XI"
        mid="Academic &nbsp; &nbsp;|"
        tabName="Admission Class XI"
      />

      <div className="innerPage">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <form
                ref={formRef}
                action={GOOGLE_SCRIPT_URL}
                method="POST"
                target="hidden_iframe"
                onSubmit={onSubmit}
                noValidate
              >
                {/* ---------------- PHOTO UPLOAD + CROP (BEGINNING) ---------------- */}
                <h3>Upload Student Photo</h3>

                <div
                  style={{
                    ...gridStyle,
                    gridTemplateColumns: "repeat(2, 1fr)",
                  }}
                >
                  <div style={fieldStyle}>
                    <label>
                      Student Photo (Upload & Crop) <span>*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoPick}
                      style={inputStyle}
                      required
                    />
                    {errors.photo && <div style={errText}>{errors.photo}</div>}
                    <div style={{ fontSize: 12, opacity: 0.75 }}>
                      Suggested: Passport size • Max 3MB
                    </div>
                  </div>

                  <div style={fieldStyle}>
                    <label>Preview</label>
                    {photoCroppedBase64 ? (
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <img
                          src={photoCroppedBase64}
                          alt="Cropped"
                          style={{
                            width: 120,
                            height: 120,
                            objectFit: "cover",
                            borderRadius: 12,
                            border: "1px solid #ddd",
                          }}
                        />
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <button
                            type="button"
                            style={btnAlt}
                            onClick={() => setShowCrop(true)}
                          >
                            Re-crop
                          </button>
                          <button type="button" style={btnAlt} onClick={removePhoto}>
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, opacity: 0.7 }}>
                        No cropped photo yet.
                      </div>
                    )}
                  </div>
                </div>

                {showCrop && (
                  <div style={cropModalOverlay}>
                    <div style={cropModalCard}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <h4 style={{ margin: 0 }}>Crop Photo</h4>
                        <button
                          type="button"
                          style={btnAlt}
                          onClick={() => setShowCrop(false)}
                        >
                          Close
                        </button>
                      </div>

                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          height: 350,
                          marginTop: 12,
                        }}
                      >
                        <Cropper
                          image={photoSrc}
                          crop={crop}
                          zoom={zoom}
                          aspect={1}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={onCropComplete}
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          marginTop: 12,
                        }}
                      >
                        <label style={{ fontSize: 13, opacity: 0.8 }}>
                          Zoom
                        </label>
                        <input
                          type="range"
                          min={1}
                          max={3}
                          step={0.1}
                          value={zoom}
                          onChange={(e) => setZoom(Number(e.target.value))}
                          style={{ width: "100%" }}
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          justifyContent: "flex-end",
                          marginTop: 14,
                        }}
                      >
                        <button type="button" style={btnAlt} onClick={removePhoto}>
                          Remove
                        </button>
                        <button type="button" style={btn} onClick={saveCroppedPhoto}>
                          Save Crop
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Fixed values */}
                <div style={rowStyle}>
                  <div style={fieldStyle}>
                    <label>Academic Year (2026–27)</label>
                    <input
                      name="classYear"
                      value="2026-27"
                      readOnly
                      style={inputStyle}
                    />
                  </div>

                  <div style={fieldStyle}>
                    <label>Grade</label>
                    <input
                      name="classValue"
                      value="Class 11"
                      readOnly
                      style={inputStyle}
                    />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Stream applying for Class 11th <span>*</span>
                    </label>
                    <select name="stream" required style={inputStyle}>
                      <option value="">Select Stream</option>
                      {streams.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <h3>Student Details</h3>
                <div style={gridStyle}>
                  <div style={fieldStyle}>
                    <label>
                      Name of the Student <span>*</span>
                    </label>
                    <input
                      name="studentName"
                      required
                      minLength={3}
                      style={inputStyle}
                    />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      DOB <span>*</span>
                    </label>
                    <input type="date" name="dob" required style={inputStyle} />
                    {errors.dob && <div style={errText}>{errors.dob}</div>}
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Email ID <span>*</span>
                    </label>
                    <input type="email" name="email" required style={inputStyle} />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Student Mobile No <span>*</span>
                    </label>
                    <input
                      name="studentMobile"
                      required
                      inputMode="numeric"
                      pattern="^[0-9]{10}$"
                      placeholder="10-digit number"
                      style={inputStyle}
                    />
                    {errors.studentMobile && (
                      <div style={errText}>{errors.studentMobile}</div>
                    )}
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Gender <span>*</span>
                    </label>
                    <select name="gender" required style={inputStyle}>
                      <option value="">Select Gender</option>
                      {genders.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
                    <label>
                      Present Residential Address <span>*</span>
                    </label>
                    <textarea
                      name="address"
                      rows={3}
                      required
                      minLength={10}
                      style={inputStyle}
                    />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Last School Attended <span>*</span>
                    </label>
                    <input name="lastSchoolAttended" required style={inputStyle} />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Affiliated to which Board <span>*</span>
                    </label>
                    <input name="boardAffiliated" required style={inputStyle} />
                  </div>
                </div>

                <h3>Father’s Details</h3>
                <div style={gridStyle}>
                  <div style={fieldStyle}>
                    <label>
                      Father’s Name <span>*</span>
                    </label>
                    <input name="fatherName" required style={inputStyle} />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Father Mobile No <span>*</span>
                    </label>
                    <input
                      name="fatherMobile"
                      required
                      inputMode="numeric"
                      pattern="^[0-9]{10}$"
                      placeholder="10-digit number"
                      style={inputStyle}
                    />
                    {errors.fatherMobile && (
                      <div style={errText}>{errors.fatherMobile}</div>
                    )}
                  </div>

                  <div style={fieldStyle}>
                    <label>Father Organization / Business / Office</label>
                    <input name="fatherOrg" style={inputStyle} />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Father’s Occupation <span>*</span>
                    </label>
                    <input name="fatherOccupation" required style={inputStyle} />
                  </div>
                </div>

                <h3>Mother’s Details</h3>
                <div style={gridStyle}>
                  <div style={fieldStyle}>
                    <label>
                      Mother’s Name <span>*</span>
                    </label>
                    <input name="motherName" required style={inputStyle} />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Mother Mobile No <span>*</span>
                    </label>
                    <input
                      name="motherMobile"
                      required
                      inputMode="numeric"
                      pattern="^[0-9]{10}$"
                      placeholder="10-digit number"
                      style={inputStyle}
                    />
                    {errors.motherMobile && (
                      <div style={errText}>{errors.motherMobile}</div>
                    )}
                  </div>

                  <div style={fieldStyle}>
                    <label>Mother Organization / Business / Office</label>
                    <input name="motherOrg" style={inputStyle} />
                  </div>

                  <div style={fieldStyle}>
                    <label>
                      Mother’s Occupation <span>*</span>
                    </label>
                    <input name="motherOccupation" required style={inputStyle} />
                  </div>
                </div>

                <h3>Sibling details (in same School)</h3>
                <p style={{ marginTop: -8, opacity: 0.75 }}>
                  Add sibling rows if applicable.
                </p>

                {sameSchoolSiblings.map((sib, idx) => (
                  <div key={`same-${idx}`} style={siblingRowStyle}>
                    <input
                      placeholder="GR NO."
                      value={sib.admNo}
                      onChange={(e) => updateSame(idx, "admNo", e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      placeholder="NAME"
                      value={sib.name}
                      onChange={(e) => updateSame(idx, "name", e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      placeholder="CLASS"
                      value={sib.className}
                      onChange={(e) =>
                        updateSame(idx, "className", e.target.value)
                      }
                      style={inputStyle}
                    />

                    {sameSchoolSiblings.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSameRow(idx)}
                        style={btnAlt}
                      >
                        Remove
                      </button>
                    )}

                    {errors[`sameSibling_${idx}`] && (
                      <div style={{ ...errText, gridColumn: "1 / -1" }}>
                        {errors[`sameSibling_${idx}`]}
                      </div>
                    )}
                  </div>
                ))}

                <button type="button" onClick={addSame} style={btn}>
                  + Add Sibling (Same School)
                </button>

                <h3 style={{ marginTop: 20 }}>
                  Sibling details (in Other School)
                </h3>
                <p style={{ marginTop: -8, opacity: 0.75 }}>
                  Add sibling rows if applicable.
                </p>

                {otherSchoolSiblings.map((sib, idx) => (
                  <div key={`other-${idx}`} style={siblingRowStyle}>
                    <input
                      placeholder="SCHOOL NAME"
                      value={sib.schoolName}
                      onChange={(e) =>
                        updateOther(idx, "schoolName", e.target.value)
                      }
                      style={inputStyle}
                    />
                    <input
                      placeholder="NAME"
                      value={sib.name}
                      onChange={(e) => updateOther(idx, "name", e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      placeholder="CLASS"
                      value={sib.className}
                      onChange={(e) =>
                        updateOther(idx, "className", e.target.value)
                      }
                      style={inputStyle}
                    />

                    {otherSchoolSiblings.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeOtherRow(idx)}
                        style={btnAlt}
                      >
                        Remove
                      </button>
                    )}

                    {errors[`otherSibling_${idx}`] && (
                      <div style={{ ...errText, gridColumn: "1 / -1" }}>
                        {errors[`otherSibling_${idx}`]}
                      </div>
                    )}
                  </div>
                ))}

                <button type="button" onClick={addOther} style={btn}>
                  + Add Sibling (Other School)
                </button>

                {/* ---------------- 10th MARKSHEET (BEFORE SUBMIT) ---------------- */}
                <h3 style={{ marginTop: 22 }}>Upload 10th Marksheet</h3>

                <div
                  style={{
                    ...gridStyle,
                    gridTemplateColumns: "repeat(2, 1fr)",
                  }}
                >
                  <div style={fieldStyle}>
                    <label>
                      10th Marksheet (PDF/Image) <span>*</span>
                    </label>
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      onChange={handleMarksheetPick}
                      style={inputStyle}
                      required
                    />
                    {errors.marksheet && (
                      <div style={errText}>{errors.marksheet}</div>
                    )}
                    <div style={{ fontSize: 12, opacity: 0.75 }}>
                      Max 5MB • PDF preferred
                    </div>
                  </div>

                  <div style={fieldStyle}>
                    <label>Selected File</label>
                    {marksheetBase64 ? (
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <div style={{ fontSize: 13 }}>✅ {marksheetFileName}</div>
                        <button type="button" style={btnAlt} onClick={removeMarksheet}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, opacity: 0.7 }}>
                        No marksheet uploaded yet.
                      </div>
                    )}
                  </div>
                </div>

                {/* Hidden sibling JSON */}
                <input type="hidden" name="sameSchoolSiblings" value={sameSchoolJson} />
                <input type="hidden" name="otherSchoolSiblings" value={otherSchoolJson} />

                {/* Hidden files (Base64 + meta) */}
                <input type="hidden" name="studentPhotoBase64" value={photoCroppedBase64} />
                <input type="hidden" name="studentPhotoFileName" value={photoFileName} />
                <input type="hidden" name="studentPhotoMimeType" value={photoMimeType} />

                <input type="hidden" name="marksheet10Base64" value={marksheetBase64} />
                <input type="hidden" name="marksheet10FileName" value={marksheetFileName} />
                <input type="hidden" name="marksheet10MimeType" value={marksheetMimeType} />

                <div style={{ marginTop: 20 }}>
                  <button type="submit" style={{ ...btn, padding: "10px 16px" }}>
                    Submit
                  </button>
                </div>

                {successMsg && (
                  <div style={{ marginTop: 12, color: "green" }}>{successMsg}</div>
                )}

                <iframe name="hidden_iframe" style={{ display: "none" }} title="hidden_iframe" />
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AdmissionClassXI;

/** basic styles */
const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 12,
  marginBottom: 12,
};

const rowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 12,
  marginBottom: 16,
};

const fieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const inputStyle = {
  padding: "10px 12px",
  border: "1px solid #ccc",
  borderRadius: 8,
  outline: "none",
  fontSize: 14,
};

const siblingRowStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr auto",
  gap: 10,
  alignItems: "center",
  marginBottom: 10,
};

const btn = {
  border: "1px solid #222",
  background: "#222",
  color: "#fff",
  padding: "8px 12px",
  borderRadius: 8,
  cursor: "pointer",
  marginTop: 6,
};

const btnAlt = {
  border: "1px solid #999",
  background: "#fff",
  color: "#222",
  padding: "8px 10px",
  borderRadius: 8,
  cursor: "pointer",
};

const errText = {
  color: "crimson",
  fontSize: 12,
  marginTop: 6,
};

const cropModalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 12,
};

const cropModalCard = {
  width: "min(760px, 96vw)",
  background: "#fff",
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
};
